import { Component } from '@theme/component';

/**
 * Galería de producto de TDM (WEB-68): imagen principal cuadrada y columna de
 * miniaturas que funciona como minimapa.
 *
 * Sin JS, la pista es un carrusel con scroll-snap y la columna una lista fija.
 * Con JS (`tdm-gallery--js`), una sola posición continua (`#position`, en
 * imágenes, de 0 a la última) coloca imágenes, miniaturas y marco con transform:
 *
 * - Imagen principal: se desliza con el dedo o arrastrando con el ratón (y con
 *   el trackpad en horizontal); al soltar se ajusta, a la siguiente si el gesto
 *   fue rápido. Solo se muestran la imagen actual y sus vecinas.
 * - Columna: el marco recorre las miniaturas siguiendo la posición y, si no
 *   caben, la columna se desplaza con él (de arriba en la primera a abajo en la
 *   última). Arrastrar sobre ella avanza desde la imagen actual como al
 *   desplazar una lista (dedo hacia arriba = siguientes), una imagen por alto de
 *   miniatura; al soltar sigue con la velocidad del gesto, frena como el scroll
 *   del navegador y se ajusta a la imagen más cercana. La rueda hacia abajo
 *   avanza con suavidad (ver `#onWheel`).
 * - Más allá de la primera o la última imagen, los gestos ceden con resistencia
 *   creciente hasta detenerse en `data-overscroll-limit` (% de una imagen), y
 *   vuelven al soltar; el marco acompaña a su miniatura. Si hay más miniaturas
 *   arriba o abajo, un degradado lo indica. Las flechas (solo escritorio, por CSS) se ocultan en
 *   la primera y la última imagen: no dan la vuelta.
 * - Las imágenes del visor se separan `--tdm-gallery-slide-gap` al pasar.
 * - Pulsar una miniatura, las flechas y el final de cada gesto usan el mismo
 *   ajuste suavizado (sin animación con movimiento reducido).
 * - Zoom con dos dedos (solo táctil): amplía la imagen dentro del visor y
 *   vuelve al soltar. Velocidad del zoom, máximo y velocidad de desplazamiento
 *   salen de los ajustes del bloque (`data-zoom-*`); la imagen ampliada nunca
 *   deja ver hueco dentro del visor.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} track
 * @property {HTMLElement} [main]
 * @property {HTMLElement[]} [slides]
 * @property {HTMLElement} [thumbList]
 * @property {HTMLButtonElement[]} [thumbs]
 * @property {HTMLElement} [frame]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
class TdmProductGallery extends Component {
  requiredRefs = ['track'];

  /** Constante de tiempo de la frenada tras soltar, en ms (como el scroll inercial). */
  static #MOMENTUM_TIME = 325;

  /** Posición continua, en imágenes; fuera de [0, última] solo durante un gesto. */
  #position = 0;

  /** Imagen marcada como activa (`aria-current`). */
  #current = 0;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /** @type {number | undefined} */
  #animationFrame;

  /** Espera a que la rueda pare para ajustar a la imagen más cercana. @type {number | undefined} */
  #wheelTimer;

  /** Destino de la rueda; la posición se desliza hacia él. @type {number | undefined} */
  #wheelTarget;

  /** Mientras dura un gesto de rueda, es de la galería aunque llegue al final. */
  #wheelLatched = false;

  /** @type {number | undefined} */
  #wheelLatchTimer;

  /**
   * Gesto en curso sobre la imagen (eje x) o la columna (eje y). `start` es la
   * posición sin resistencia al empezar; `samples` sirve para la velocidad.
   * @type {{ axis: 'x' | 'y', pointerId: number, startX: number, startY: number, start: number, active: boolean, samples: { t: number, p: number }[] } | undefined}
   */
  #drag;

  /** Tras un gesto, el clic que lo acompaña no debe seleccionar nada. */
  #suppressClick = false;

  /** @type {{ image: HTMLElement, distance: number, x: number, y: number, originX: number, originY: number, size: number } | undefined} */
  #pinch;

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#setup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#teardown();
  }

  /** @param {Event} event */
  select(event) {
    if (this.#suppressClick) return;
    if (!(event.target instanceof HTMLElement)) return;

    const index = Number(event.target.dataset.index);
    if (Number.isInteger(index)) this.goTo(index);
  }

  previous() {
    this.goTo(this.#current - 1);
  }

  next() {
    this.goTo(this.#current + 1);
  }

  /** @param {number} index */
  goTo(index) {
    this.#animateTo(index);
  }

  #setup() {
    this.#teardown();
    if (this.#count < 2) return;

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const { track, thumbList, main } = this.refs;

    this.classList.add('tdm-gallery--js');

    track.addEventListener('pointerdown', (event) => this.#dragStart(event, 'x'), { signal });
    track.addEventListener('pointermove', (event) => this.#dragMove(event), { signal });
    track.addEventListener('pointerup', (event) => this.#dragEnd(event), { signal });
    track.addEventListener('pointercancel', (event) => this.#dragEnd(event), { signal });
    track.addEventListener('wheel', (event) => this.#onWheel(event, 'x'), { signal, passive: false });
    track.addEventListener('dragstart', (event) => event.preventDefault(), { signal });

    // La posición la llevan los transform: si el navegador desplaza la pista o
    // la columna (foco, scrollIntoView), las imágenes dejarían de coincidir con el
    // marco. `overflow: clip` lo impide; esto cubre los navegadores sin `clip`.
    for (const element of [track, thumbList]) {
      element?.addEventListener(
        'scroll',
        () => {
          element.scrollLeft = 0;
          element.scrollTop = 0;
        },
        { signal, passive: true }
      );
    }

    if (thumbList) {
      thumbList.addEventListener('pointerdown', (event) => this.#dragStart(event, 'y'), { signal });
      thumbList.addEventListener('pointermove', (event) => this.#dragMove(event), { signal });
      thumbList.addEventListener('pointerup', (event) => this.#dragEnd(event), { signal });
      thumbList.addEventListener('pointercancel', (event) => this.#dragEnd(event), { signal });
      thumbList.addEventListener('wheel', (event) => this.#onWheel(event, 'y'), { signal, passive: false });
    }

    if (main) {
      main.addEventListener('touchstart', (event) => this.#pinchStart(event), { signal, passive: false });
      main.addEventListener('touchmove', (event) => this.#pinchMove(event), { signal, passive: false });
      main.addEventListener('touchend', (event) => this.#pinchEnd(event), { signal });
      main.addEventListener('touchcancel', (event) => this.#pinchEnd(event), { signal });
      // Safari: evita que el gesto amplíe la página.
      main.addEventListener('gesturestart', (event) => event.preventDefault(), { signal, passive: false });
    }

    this.#resizeObserver = new ResizeObserver(() => this.#render());
    this.#resizeObserver.observe(track);
    if (thumbList) this.#resizeObserver.observe(thumbList);

    this.#render();
  }

  #teardown() {
    this.#abortController?.abort();
    this.#abortController = undefined;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = undefined;
    this.#stopAnimation();
    clearTimeout(this.#wheelTimer);
    clearTimeout(this.#wheelLatchTimer);
    this.#wheelLatched = false;
    this.#drag = undefined;
    this.#pinch = undefined;
  }

  get #count() {
    return this.refs.slides?.length ?? 0;
  }

  get #last() {
    return Math.max(this.#count - 1, 0);
  }

  get #direction() {
    return getComputedStyle(this).direction === 'rtl' ? -1 : 1;
  }

  /** @param {number} position */
  #clamp(position) {
    return Math.min(Math.max(position, 0), this.#last);
  }

  /**
   * Más allá de los extremos, el gesto sigue al dedo cada vez menos hasta
   * quedarse quieto en el límite: la elasticidad baja hasta cero.
   * @param {number} position
   */
  #resist(position) {
    const clamped = this.#clamp(position);
    const limit = (Number(this.dataset.overscrollLimit) || 0) / 100;
    const over = position - clamped;
    if (limit === 0 || over === 0) return clamped;

    return clamped + Math.sign(over) * limit * (1 - Math.exp(-Math.abs(over) / limit));
  }

  /** Distancia entre dos imágenes del visor: su ancho más la separación. */
  get #slideStep() {
    const gap = parseFloat(getComputedStyle(this).getPropertyValue('--tdm-gallery-slide-gap')) || 0;
    return this.refs.track.clientWidth + gap || 1;
  }

  /** Medidas de la columna; el paso es la distancia entre dos miniaturas. */
  #metrics() {
    const { thumbList, thumbs = [] } = this.refs;
    const first = thumbs[0]?.closest('li');
    if (!thumbList || !first) return null;

    const gap = parseFloat(getComputedStyle(this).getPropertyValue('--tdm-gallery-thumb-gap')) || 0;
    const thumbHeight = first.offsetHeight;
    const step = thumbHeight + gap || 1;
    const viewHeight = thumbList.clientHeight;

    return { step, thumbHeight, viewHeight, overflows: this.#last * step + thumbHeight > viewHeight + 1 };
  }

  #render() {
    const count = this.#count;
    if (count === 0) return;

    const position = this.#position;
    const { slides = [], thumbs = [], frame } = this.refs;
    const width = this.#slideStep;
    const direction = this.#direction;

    for (const [i, slide] of slides.entries()) {
      const offset = i - position;

      // Solo la imagen actual y sus vecinas: las demás ni se pintan ni se descargan.
      const visible = Math.abs(offset) < 1.5;
      slide.hidden = !visible;
      if (visible) slide.style.transform = `translateX(${offset * width * direction}px)`;
    }

    // Si la columna no cabe, se desplaza de forma que el marco recorra su alto
    // visible: arriba en la primera imagen, abajo en la última.
    const metrics = this.#metrics();
    if (metrics) {
      const clamped = this.#clamp(position);
      const ratio = this.#last ? clamped / this.#last : 0;
      const scroll = metrics.overflows
        ? clamped * metrics.step - ratio * (metrics.viewHeight - metrics.thumbHeight)
        : 0;
      // Más allá de un extremo se mueve toda la lista, con el marco sobre su miniatura.
      const offset = scroll + (position - clamped) * metrics.step;

      for (const [i, thumb] of thumbs.entries()) {
        const item = thumb.closest('li');
        if (item) item.style.transform = `translateY(${i * metrics.step - offset}px)`;
      }
      if (frame) frame.style.transform = `translateY(${clamped * metrics.step - offset}px)`;

      // Cada degradado aparece según lo que quede por ver en su lado.
      const maxScroll = this.#last * metrics.step + metrics.thumbHeight - metrics.viewHeight;
      const fade = metrics.thumbHeight / 2 || 1;
      this.style.setProperty('--tdm-gallery-fade-start', String(metrics.overflows ? Math.min(scroll / fade, 1) : 0));
      this.style.setProperty(
        '--tdm-gallery-fade-end',
        String(metrics.overflows ? Math.min((maxScroll - scroll) / fade, 1) : 0)
      );
    }

    const index = Math.round(this.#clamp(position));
    if (index !== this.#current) this.#setCurrent(index);
  }

  /** @param {number} position */
  #setPosition(position) {
    this.#position = position;
    this.#render();
  }

  /** @param {number} index */
  #setCurrent(index) {
    this.#current = index;

    for (const [i, thumb] of (this.refs.thumbs ?? []).entries()) {
      if (i === index) {
        thumb.setAttribute('aria-current', 'true');
      } else {
        thumb.removeAttribute('aria-current');
      }
    }

    // Sin flecha hacia fuera en los extremos; si tenía el foco, pasa a la otra.
    const { previous, next } = this.refs;
    if (!previous || !next) return;

    const hidePrevious = index === 0;
    const hideNext = index === this.#last;
    if (hidePrevious && document.activeElement === previous) next.focus();
    if (hideNext && document.activeElement === next) previous.focus();
    previous.hidden = hidePrevious;
    next.hidden = hideNext;
  }

  /**
   * Ajuste suavizado hasta la imagen `target`. Con `velocity` (imágenes/ms), la
   * animación arranca a esa velocidad y frena hasta parar, como el scroll
   * inercial; sin ella, dura según la distancia.
   * @param {number} target
   * @param {number} [velocity]
   */
  #animateTo(target, velocity = 0) {
    this.#stopAnimation();

    const from = this.#position;
    const to = this.#clamp(Math.round(target));
    const distance = to - from;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (Math.abs(distance) < 0.001 || reduceMotion) {
      this.#setPosition(to);
      return;
    }

    // Una curva ease-out cúbica sale con pendiente 3 · distancia / duración.
    const followsGesture = velocity !== 0 && Math.sign(velocity) === Math.sign(distance);
    const duration = followsGesture
      ? Math.min(Math.max((3 * Math.abs(distance)) / Math.abs(velocity), 250), 1200)
      : Math.min(250 + 80 * Math.abs(distance), 600);
    const start = performance.now();

    /** @param {number} now */
    const step = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      this.#setPosition(from + distance * (1 - (1 - progress) ** 3));

      if (progress < 1) {
        this.#animationFrame = requestAnimationFrame(step);
      } else {
        this.#animationFrame = undefined;
      }
    };

    this.#animationFrame = requestAnimationFrame(step);
  }

  #stopAnimation() {
    if (this.#animationFrame !== undefined) cancelAnimationFrame(this.#animationFrame);
    this.#animationFrame = undefined;
    this.#wheelTarget = undefined;
  }

  /**
   * @param {PointerEvent} event
   * @param {'x' | 'y'} axis
   */
  #dragStart(event, axis) {
    if (this.#pinch || !event.isPrimary) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    // Las flechas están sobre la imagen: su clic no es un deslizamiento.
    if (axis === 'x' && event.target instanceof Element && event.target.closest('button')) return;

    this.#suppressClick = false;
    this.#drag = {
      axis,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      start: this.#position,
      active: false,
      samples: [],
    };
  }

  /** @param {PointerEvent} event */
  #dragMove(event) {
    const drag = this.#drag;
    if (!drag || event.pointerId !== drag.pointerId || this.#pinch) return;

    const dx = event.clientX - drag.startX;
    const dy = event.clientY - drag.startY;

    if (!drag.active) {
      const along = drag.axis === 'x' ? Math.abs(dx) : Math.abs(dy);
      // Un toque que apenas se mueve sigue siendo un clic; en la imagen, un
      // gesto vertical lo deja al navegador para desplazar la página.
      if (along < 6 || (drag.axis === 'x' && Math.abs(dx) < Math.abs(dy))) return;

      drag.active = true;
      this.#stopAnimation();
      drag.start = this.#position;
      drag.startX = event.clientX;
      drag.startY = event.clientY;
      drag.samples = [{ t: event.timeStamp, p: drag.start }];

      const element = drag.axis === 'x' ? this.refs.track : this.refs.thumbList;
      try {
        element?.setPointerCapture(event.pointerId);
      } catch {
        // El puntero ya no está activo: el gesto sigue sin captura.
      }
      return;
    }

    // En la imagen, el contenido sigue al dedo; en la columna, como una lista:
    // subir el dedo avanza, una imagen por alto de miniatura.
    const raw =
      drag.axis === 'x'
        ? drag.start - (dx * this.#direction) / this.#slideStep
        : drag.start - dy / (this.#metrics()?.step || 1);

    drag.samples.push({ t: event.timeStamp, p: raw });
    while (drag.samples.length > 2 && event.timeStamp - (drag.samples[0]?.t ?? 0) > 100) drag.samples.shift();

    this.#setPosition(this.#resist(raw));
  }

  /** @param {PointerEvent} event */
  #dragEnd(event) {
    const drag = this.#drag;
    if (!drag || event.pointerId !== drag.pointerId) return;

    this.#drag = undefined;
    if (!drag.active) return;

    // El clic que acompaña al gesto llega justo después de soltar.
    this.#suppressClick = true;
    setTimeout(() => (this.#suppressClick = false));

    // Velocidad de los últimos ~100 ms, en imágenes por ms; si el dedo se paró antes de soltar, cero.
    const first = drag.samples[0];
    const last = drag.samples[drag.samples.length - 1];
    const moving = first && last && last.t > first.t && event.timeStamp - last.t < 100;
    const velocity = moving ? (last.p - first.p) / (last.t - first.t) : 0;
    const position = this.#position;

    if (drag.axis === 'x') {
      // Imagen: un gesto rápido pasa a la siguiente aunque no llegue a la mitad.
      const base = Math.round(position);
      const flick = Math.abs(velocity) > 0.002 && base === Math.round(drag.start);
      this.#animateTo(flick ? base + Math.sign(velocity) : base);
      return;
    }

    // Columna: sigue con la velocidad del gesto, frena y se ajusta a la más cercana.
    this.#animateTo(position + velocity * TdmProductGallery.#MOMENTUM_TIME, velocity);
  }

  /**
   * Rueda o trackpad: en la columna, hacia abajo avanza; en la imagen, el
   * desplazamiento horizontal. La rueda mueve un destino y la posición se
   * desliza hacia él (un clic de rueda ≈ una imagen); cuando el gesto para, el
   * destino se ajusta a la imagen más cercana.
   *
   * Un gesto que empieza en un extremo y empuja hacia fuera desplaza la página.
   * Uno que empezó en la galería sigue siendo suyo hasta que la rueda para,
   * aunque llegue al final, para que la página no se mueva por sorpresa.
   * @param {WheelEvent} event
   * @param {'x' | 'y'} axis
   */
  #onWheel(event, axis) {
    if (axis === 'x' && Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;

    const raw = axis === 'y' ? event.deltaY : event.deltaX * this.#direction;
    const delta = event.deltaMode === WheelEvent.DOM_DELTA_LINE ? raw * 16 : raw;
    if (delta === 0) return;

    // Un clic de rueda de ratón (saltos grandes o por líneas) es exactamente una
    // imagen; el trackpad (saltos pequeños y seguidos) sigue el gesto.
    const notch = event.deltaMode !== WheelEvent.DOM_DELTA_PIXEL || Math.abs(raw) >= 50;
    const unit = axis === 'y' ? this.#metrics()?.step || 1 : this.#slideStep;
    const from = this.#wheelTarget ?? this.#position;
    const target = this.#clamp(notch ? Math.round(from) + Math.sign(delta) : from + delta / unit);

    if (!this.#wheelLatched && Math.abs(target - from) < 0.001) return;

    event.preventDefault();
    this.#wheelLatched = true;
    clearTimeout(this.#wheelLatchTimer);
    this.#wheelLatchTimer = setTimeout(() => (this.#wheelLatched = false), 300);

    if (this.#wheelTarget === undefined) {
      // Otro ajuste en curso (clic, arrastre) cede el paso a la rueda.
      this.#stopAnimation();
      this.#wheelTarget = target;
      this.#glide();
    } else {
      this.#wheelTarget = target;
    }

    clearTimeout(this.#wheelTimer);
    this.#wheelTimer = setTimeout(() => {
      if (this.#wheelTarget !== undefined) this.#wheelTarget = Math.round(this.#wheelTarget);
    }, 120);
  }

  /** Desliza la posición hacia el destino de la rueda, frenando al acercarse. */
  #glide() {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let last = performance.now();

    /** @param {number} now */
    const step = (now) => {
      const target = this.#wheelTarget;
      if (target === undefined) return;

      // Cubre el 1 - e^(-dt/τ) de la distancia en cada fotograma (τ = 70 ms).
      const factor = reduceMotion ? 1 : 1 - Math.exp(-(now - last) / 70);
      last = now;
      const position = this.#position + (target - this.#position) * factor;

      // Parado sobre una imagen y sin rueda pendiente: termina.
      if (Math.abs(target - position) < 0.002 && Number.isInteger(target)) {
        this.#animationFrame = undefined;
        this.#wheelTarget = undefined;
        this.#setPosition(target);
        return;
      }

      this.#setPosition(position);
      this.#animationFrame = requestAnimationFrame(step);
    };

    this.#animationFrame = requestAnimationFrame(step);
  }

  /** @param {TouchEvent} event */
  #pinchStart(event) {
    if (event.touches.length !== 2) return;

    const image = this.refs.slides?.[this.#current]?.querySelector('img');
    if (!(image instanceof HTMLElement)) return;

    event.preventDefault();

    // El pellizco manda sobre el deslizamiento que hubiera empezado.
    if (this.#drag) {
      this.#drag = undefined;
      this.#animateTo(this.#position);
    }

    const [a, b] = event.touches;
    if (!a || !b) return;

    const x = (a.clientX + b.clientX) / 2;
    const y = (a.clientY + b.clientY) / 2;
    const rect = image.getBoundingClientRect();

    image.style.transition = 'none';
    image.style.transformOrigin = '0 0';

    this.#pinch = {
      image,
      distance: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY) || 1,
      x,
      y,
      originX: x - rect.left,
      originY: y - rect.top,
      size: rect.width,
    };
  }

  /** @param {TouchEvent} event */
  #pinchMove(event) {
    const pinch = this.#pinch;
    if (!pinch) return;

    event.preventDefault();

    const [a, b] = event.touches;
    if (!a || !b) return;

    const max = Number(this.dataset.zoomMax) || 4;
    const speed = Number(this.dataset.zoomSpeed) || 1;
    const panSpeed = Number(this.dataset.zoomPanSpeed) || 1;

    // La velocidad multiplica lo que se separan los dedos: con 2x, abrirlos al doble amplía 3x.
    const spread = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY) / pinch.distance;
    const scale = Math.min(Math.max(1 + (spread - 1) * speed, 1), max);

    // El punto que estaba entre los dedos sigue entre ellos, y mover los dedos
    // desplaza la imagen `panSpeed` veces más rápido. Sin dejar hueco en el visor.
    const panX = ((a.clientX + b.clientX) / 2 - pinch.x) * panSpeed;
    const panY = ((a.clientY + b.clientY) / 2 - pinch.y) * panSpeed;
    const limit = pinch.size * (1 - scale);
    const tx = Math.min(Math.max(pinch.originX * (1 - scale) + panX, limit), 0);
    const ty = Math.min(Math.max(pinch.originY * (1 - scale) + panY, limit), 0);

    pinch.image.style.transform = `translate(${tx}px, ${ty}px) scale(${scale})`;
  }

  /** @param {TouchEvent} event */
  #pinchEnd(event) {
    const pinch = this.#pinch;
    if (!pinch || event.touches.length >= 2) return;

    this.#pinch = undefined;

    const { image } = pinch;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    image.style.transition = reduceMotion ? 'none' : 'transform 200ms ease-out';
    image.style.transform = '';
    image.addEventListener(
      'transitionend',
      () => {
        image.style.transition = '';
        image.style.transformOrigin = '';
      },
      { once: true }
    );
  }
}

if (!customElements.get('tdm-product-gallery')) {
  customElements.define('tdm-product-gallery', TdmProductGallery);
}
